import { useState } from "react";
import { Eye, Pencil, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button, LinkButton } from "../../../Components/Buttons";
import { ROUTE_PATHS } from "../../../constants/routeConstants";

const MOCK = [
  {
    id: 1,
    name: "Welcome Email",
    category: "transactional",
    subject: "Welcome to Ukasha!",
    status: true,
    body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:32px 24px;background:#fff;">
  <h2 style="color:#f97316;margin-bottom:8px;">Welcome to Ukasha! 🎉</h2>
  <p style="color:#374151;">Hi <strong>{{name}}</strong>,</p>
  <p style="color:#374151;">We're thrilled to have you on board. Explore our curated collection of premium lifestyle products.</p>
  <a href="#" style="display:inline-block;margin-top:16px;padding:10px 24px;background:#f97316;color:#fff;border-radius:6px;text-decoration:none;font-weight:600;">Shop Now</a>
  <hr style="margin:32px 0;border:none;border-top:1px solid #e5e7eb;" />
  <p style="color:#9ca3af;font-size:12px;">© 2026 Ukasha. All rights reserved.</p>
</div>`,
  },
  {
    id: 2,
    name: "Password Reset",
    category: "transactional",
    subject: "Reset Your Password",
    status: true,
    body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:32px 24px;background:#fff;">
  <h2 style="color:#374151;">Password Reset Request</h2>
  <p style="color:#374151;">Hi <strong>{{name}}</strong>,</p>
  <p style="color:#374151;">We received a request to reset your password. Click below to proceed:</p>
  <a href="{{reset_link}}" style="display:inline-block;margin-top:16px;padding:10px 24px;background:#3b82f6;color:#fff;border-radius:6px;text-decoration:none;font-weight:600;">Reset Password</a>
  <p style="color:#6b7280;font-size:13px;margin-top:24px;">If you didn't request this, you can safely ignore this email.</p>
  <hr style="margin:32px 0;border:none;border-top:1px solid #e5e7eb;" />
  <p style="color:#9ca3af;font-size:12px;">© 2026 Ukasha. All rights reserved.</p>
</div>`,
  },
  {
    id: 3,
    name: "Order Confirmation",
    category: "transactional",
    subject: "Your Order #{{order_id}} is Confirmed",
    status: true,
    body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:32px 24px;background:#fff;">
  <h2 style="color:#10b981;">Order Confirmed ✓</h2>
  <p style="color:#374151;">Hi <strong>{{name}}</strong>, your order <strong>#{{order_id}}</strong> has been confirmed.</p>
  <p style="color:#374151;">Estimated delivery: <strong>{{delivery_date}}</strong></p>
  <hr style="margin:32px 0;border:none;border-top:1px solid #e5e7eb;" />
  <p style="color:#9ca3af;font-size:12px;">© 2026 Ukasha. All rights reserved.</p>
</div>`,
  },
  {
    id: 4,
    name: "Newsletter",
    category: "marketing",
    subject: "Ukasha Newsletter — {{month}} Edition",
    status: false,
    body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;padding:32px 24px;background:#fff;">
  <h2 style="color:#f97316;">This Month at Ukasha</h2>
  <p style="color:#374151;">Hi <strong>{{name}}</strong>,</p>
  <p style="color:#374151;">{{content}}</p>
  <a href="#" style="display:inline-block;margin-top:16px;padding:10px 24px;background:#f97316;color:#fff;border-radius:6px;text-decoration:none;font-weight:600;">Explore Now</a>
  <hr style="margin:32px 0;border:none;border-top:1px solid #e5e7eb;" />
  <p style="color:#9ca3af;font-size:12px;">© 2026 Ukasha. All rights reserved.</p>
</div>`,
  },
];

const MOCK_PAGINATION = (page, limit, data) => ({
  current_page: page,
  total_pages: Math.ceil(data.length / limit),
  limit,
  data: data.slice((page - 1) * limit, page * limit),
});

const HEADERS = [
  { key: "id", label: "#" },
  { key: "name", label: "Template Name" },
  { key: "category", label: "Category" },
  { key: "subject", label: "Subject" },
  { key: "status", label: "Status", type: "status" },
];

const PreviewModal = ({ template, onClose }) => {
  if (!template) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
          <div>
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{template.name}</p>
            <p className="text-xs text-gray-400 mt-0.5">Subject: {template.subject}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 transition"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Email preview in iframe for isolation */}
        <div className="flex-1 overflow-auto p-4 bg-gray-50 dark:bg-slate-800">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <iframe
              srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0;padding:0;background:#f9fafb;">${template.body}</body></html>`}
              title="Email Preview"
              className="w-full border-0"
              style={{ minHeight: "480px" }}
              onLoad={(e) => {
                const doc = e.target.contentDocument;
                if (doc) e.target.style.height = doc.body.scrollHeight + "px";
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const EmailTemplateList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(MOCK);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);

  const filtered = search.trim()
    ? data.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
    : data;
  const paginationData = MOCK_PAGINATION(page, limit, filtered);

  const handleDelete = (row) => setData((prev) => prev.filter((r) => r.id !== row.id));
  const handleBulk = (rows) => {
    const ids = rows.map((r) => r.id);
    setData((prev) => prev.filter((r) => !ids.includes(r.id)));
  };

  return (
    <>
      <BreadCrumb title="Email Templates" />
      <TableListing
        headers={HEADERS}
        paginationData={paginationData}
        handleBulk={handleBulk}
        search={search}
        setSearch={(v) => { setSearch(v); setPage(1); }}
        onPageChange={setPage}
        onRowsPerPageChange={setLimit}
        trashed={false}
        setTrashed={() => {}}
        createButton
        createLink={ROUTE_PATHS.CREATE_EMAIL_TEMPLATE}
        actions={(row) => (
          <div className="flex gap-1">
            <Button
              onClick={() => setPreview(row)}
              title="Preview"
              icon={Eye}
              className="text-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-500/10"
            />
            <Button
              onClick={() => navigate(ROUTE_PATHS.CREATE_EMAIL_TEMPLATE, { state: { template: row } })}
              title="Edit"
              icon={Pencil}
              className="text-green-600 hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-500/10"
            />
            <Button
              onClick={() => handleDelete(row)}
              title="Delete"
              icon={Trash2}
              className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10"
            />
          </div>
        )}
      />

      <PreviewModal template={preview} onClose={() => setPreview(null)} />
    </>
  );
};

export default EmailTemplateList;
